import { isEqual } from 'lodash-es';
import { UserFilterListItem } from '@/models/user/userFilter';
import { FilterType } from '@/models/filter/FilterType';
import { FilterState } from '@/types/filter';
import { rehydrateFilterState, getDefaultFiltersState } from '@/context/FiltersContext/FiltersContextProvider';
import { AppliedFilterSetStorage } from '@/utils/filter/appliedFilterSetStorage';
import applyLocalFiltersToStorage from '@/utils/filter/applyLocalFiltersToStorage';
import { LOCAL_FILTERS_STORAGE_MAP, LOCAL_FILTERS_KEY_INSIGHTS } from '@/context/LocalFiltersRegistry/localFiltersKeys';
import fetchFilterPayload from '@/utils/filter/fetchFilterPayload';
import { writeCachedCountryCodes } from '@/models/filter/configs/general/countryFilter';
import { OptionPathsByFilter } from '@/context/FiltersContext/FiltersContext';
import { recalculatePresetDateRanges } from '@/hooks/useApplySharedView.utils';

const RETRY_DELAY_MS = 200;
const MAX_RETRIES = 3;

/**
 * Reads current local filter values from sessionStorage to verify they
 * match the intended payload. Insights filters use a Zustand store and
 * are excluded from this check.
 */
const readLocalFiltersFromStorage = (): Record<string, Record<string, unknown>> => {
  const result: Record<string, Record<string, unknown>> = {};
  for (const [key, storageKey] of Object.entries(LOCAL_FILTERS_STORAGE_MAP)) {
    if (key === LOCAL_FILTERS_KEY_INSIGHTS) continue;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        result[key] = JSON.parse(raw);
      }
    } catch {
      // Skip unreadable entries
    }
  }
  return result;
};

/**
 * Applies local filters and retries if side-effects from the global filter
 * change overwrite them. The handleApplyFilter call triggers API re-fetches
 * and effects (e.g. PartnerScoreFilter prune/auto-select) that can
 * overwrite local filters set moments earlier.
 */
export const applyLocalFiltersWithRetry = (
  payload: Record<string, Record<string, unknown>>,
  remainingAttempts: number = MAX_RETRIES,
): void => {
  applyLocalFiltersToStorage(payload);
  if (remainingAttempts <= 0) return;

  setTimeout(() => {
    const current = readLocalFiltersFromStorage();
    const expected: Record<string, Record<string, unknown>> = {};
    for (const [key] of Object.entries(LOCAL_FILTERS_STORAGE_MAP)) {
      if (key === LOCAL_FILTERS_KEY_INSIGHTS) continue;
      expected[key] = payload[key] ?? {};
    }
    if (!isEqual(current, expected)) {
      applyLocalFiltersWithRetry(payload, remainingAttempts - 1);
    }
  }, RETRY_DELAY_MS);
};

interface FetchAndApplyFilterParams {
  filterId: string;
  filterMetadata: UserFilterListItem;
  filterConfig: readonly FilterType[];
  handleApplyFilter: (filters: FilterState) => void;
  replaceOptionPaths?: (paths: OptionPathsByFilter) => void;
}

const fetchAndApplyFilter = async ({
  filterId,
  filterMetadata,
  filterConfig,
  handleApplyFilter,
  replaceOptionPaths,
}: FetchAndApplyFilterParams): Promise<void> => {
  const {
    globalFilters: rawGlobalFilters,
    localFilters,
    resolvedCountryCodes,
    optionPaths,
  } = await fetchFilterPayload(filterId);
  // Preset date ranges (Cycle to Date, YTD, ...) are stored with the dates of
  // the save day. Re-resolve them against today, as the manual apply path does,
  // so a default filter applied on app load stays "to date".
  const globalFilters = recalculatePresetDateRanges(rawGlobalFilters);
  const localFiltersPayload = localFilters ?? {};

  // Seed the country code cache from the payload. On initial load the country
  // filter's dropdown options aren't fetched yet, so without this the filter
  // can't resolve its Cube country_code values and is dropped from queries.
  if (resolvedCountryCodes?.length) {
    writeCachedCountryCodes(resolvedCountryCodes);
  }

  // Same reasoning for hierarchical filters: their option queries have not run,
  // so without the saved paths `applyFilter` would build a different query than
  // the one the filter was saved with. This is a whole-filter replacement, so a
  // payload without paths must clear the registry rather than keep the previous
  // selection's — otherwise the new selection is evaluated against a stale
  // hierarchy.
  if (replaceOptionPaths) {
    replaceOptionPaths(optionPaths ?? {});
  }

  const defaultState = getDefaultFiltersState(filterConfig);
  const rehydrated = rehydrateFilterState(globalFilters, defaultState);

  applyLocalFiltersWithRetry(localFiltersPayload);
  handleApplyFilter(rehydrated);

  AppliedFilterSetStorage.save(filterMetadata, rehydrated, localFiltersPayload);
};

export default fetchAndApplyFilter;
